import type { User } from '@/types/auth';

/**
 * Check if a user has a specific permission
 * @param user - The user object containing permission_list
 * @param permission - The permission name to check
 * @returns boolean - true if user has the permission
 */
export const hasPermission = (user: User | null, permission: string): boolean => {
  if (!user) return false;
  
  // Check permission_list array
  if (user.permission_list && Array.isArray(user.permission_list)) {
    return user.permission_list.includes(permission);
  }
  
  return false;
};

/**
 * Check if a user has any of the specified permissions
 * @param user - The user object containing permission_list
 * @param permissions - Array of permission names to check
 * @returns boolean - true if user has at least one of the permissions
 */
export const hasAnyPermission = (user: User | null, permissions: string[]): boolean => {
  if (!user || !permissions.length) return false;
  
  return permissions.some(permission => hasPermission(user, permission));
};

/**
 * Check if a user has all of the specified permissions
 * @param user - The user object containing permission_list
 * @param permissions - Array of permission names to check
 * @returns boolean - true if user has all of the permissions
 */
export const hasAllPermissions = (user: User | null, permissions: string[]): boolean => {
  if (!user || !permissions.length) return false;
  
  return permissions.every(permission => hasPermission(user, permission));
};

/**
 * Get the team_auth data from localStorage
 * @returns The parsed team_auth object or null
 */
export const getTeamAuth = (): {
  user_id: string;
  user_name: string;
  user_email: string;
  user_role: string;
  is_active: boolean;
  is_merchant: boolean;
  last_login: string;
  permission_list: string[];
} | null => {
  try {
    const teamAuthStr = localStorage.getItem('team_auth');
    if (!teamAuthStr) return null;
    return JSON.parse(teamAuthStr);
  } catch {
    return null;
  }
};

/**
 * Check if team user is authenticated by checking team_auth in localStorage
 * @returns boolean - true if team_auth exists in localStorage
 */
export const isTeamUserAuthenticated = (): boolean => {
  const teamAuth = getTeamAuth();
  return teamAuth !== null && teamAuth.is_active === true;
};

/**
 * Check permission from team_auth localStorage directly
 * Useful for checking permissions before Redux state is initialized
 * @param permission - The permission name to check
 * @returns boolean - true if team user has the permission
 */
export const teamAuthHasPermission = (permission: string): boolean => {
  const teamAuth = getTeamAuth();
  if (!teamAuth || !teamAuth.permission_list) return false;
  return teamAuth.permission_list.includes(permission);
};
